import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import {
  Grid,
  Table,
} from "semantic-ui-react";
import {
  AccordionTitleView,
  CommonTable,
  CustomButton,
  DropDown,
} from "../../../../../components";
import "../styles/CultivarForm.scss";
import { InputNumber } from "../../../../../components/InputText/InputNumber";
import { MdDeleteOutline, MdModeEditOutline } from "react-icons/md";
import { isEmpty } from "lodash";
import PlusIcon from "../../../../../assets/commonIcon/PlusIcon";
import { CULTIVATION_TYPE, IrrigationTableHeader } from "../../../../../config/constants";
import { useGetIrrigationMethod, useGetPowerSource } from "../../../../../api/static-data";
import { useForm } from "react-hook-form";
import { isCheckPercentageIrrigation, validateCultivationNumber } from "../../../../../utils/utils";
import ConfirmModal from "../../../../../components/confirmViewModal/ConfirmModal";

const Irrigation = forwardRef((
  {
    currentFormData,
    currentDataType,
    currentOriginalFormData
  }: any,
  ref
) => {
  const [accordionEnabled, setAccordionEnabled] = useState<boolean>(false);
  const [enableUpdateButton, setEnableUpdateButton] = useState<any>(false);
  const [irrigation, setIrrigation] = useState<any>("");
  const [powerSource, setPowerSource] = useState<any>("")
  const [irrigationListData, setIrrigationListData] = useState<any>([])
  const [onIrrigationEdit, setOnIrrigationEdit] = useState(false);
  const [editIndex, setEditIndex] = useState<any>(null);
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const [deleteValue, setDeleteValue] = useState<any>({
    item: "",
    type: "",
  });

  const {
    data: irrigationData,
  } = useGetIrrigationMethod();

  const {
    data: powerSourceData,
  } = useGetPowerSource();

  const irrigationDetails = useCallback(() => {
    const irrigationTableData = currentFormData?.cultivationField?.irrigations?.map(
      (item: any) => {
        return {
          ...item,
          id: parseInt(item.id),
        };
      }
    );
    setIrrigationListData(irrigationTableData)
  }, [currentFormData])

  useEffect(() => {
    irrigationDetails()
  }, [currentFormData])

  const values = useMemo(() => {
    return {
      waterAmount: "",
      appliedPortion: ""
    }
  }, [currentFormData])

  const {
    formState: { errors },
    control,
    setValue,
    handleSubmit
  } = useForm({ defaultValues: values, values: values });

  const resetIrrigation = () => {
    setIrrigation("");
    setPowerSource("");
    setValue("waterAmount", "");
    setValue("appliedPortion", "");
  };

  const AddIrrigation = handleSubmit((values) => {
    const { waterAmount, appliedPortion } = values;

    const formattedData = {
      irrigationMethodId: Number(irrigation),
      powerSourceId: Number(powerSource),
      waterAmount: Number(waterAmount),
      appliedPortion: Number(appliedPortion),
    };

    if (onIrrigationEdit) {
      const updatedList = [...irrigationListData];
      updatedList[editIndex] = {
        ...updatedList[editIndex],
        ...formattedData,
      };
      setIrrigationListData(updatedList);
      setOnIrrigationEdit(false);
    } else {
      setIrrigationListData([...irrigationListData, formattedData]);
    }
    resetIrrigation();
  });

  const onEditHandler = (item: any, index: any) => {
    setOnIrrigationEdit(true);
    setEditIndex(index);
    setValue("waterAmount", item?.waterAmount ?? "");
    setValue("appliedPortion", item?.appliedPortion ?? "");
    setIrrigation(item.irrigationMethodId.toString());
    setPowerSource(item.powerSourceId.toString());
  }

  const deleteIrrigation = useCallback(() => {
    setIrrigationListData(
      irrigationListData.filter((_: any, index: number) => index !== deleteValue.index)
    )
    setVisibleDeleteModal(false);
    setDeleteValue({});
  }, [deleteValue])

  useImperativeHandle(ref, () => ({
    getIrrigationData: () => {
      return irrigationListData
    }
  }));

  const handleClick = () => {
    setAccordionEnabled(!accordionEnabled);
  };

  const originalIrrigation =
    currentOriginalFormData?.cultivationField?.irrigations || [];

  return currentDataType === CULTIVATION_TYPE.Supplier ? (
    <div>
      <AccordionTitleView
        accordionEnabled={false}
        title={"Irrigation Details"}
        plusIcon={false}
      />
      <div>
        <Grid>
          <Grid.Row divided>
            <Grid.Column computer={8} tablet={16} mobile={16}>
              {originalIrrigation.length > 0 && (
                <Grid.Column
                  computer={16}
                  tablet={16}
                  mobile={16}
                  className="pesticideTable"
                >
                  <CommonTable
                    tableHeaderData={IrrigationTableHeader.filter(
                      (e: any) => e.name !== ""
                    )}
                    className="pesticideTable"
                    singleLine={false}
                    isSimulateView
                  >
                    {originalIrrigation.map((item: any, index: any) => {
                      return (
                        <Table.Row key={`pesticide_${item?.id}`}>
                          <Table.Cell>
                            <p>
                              {irrigationData?.find(
                                (e: any) => e.value == item.irrigationMethodId
                              ).text ?? ""}
                            </p>
                          </Table.Cell>
                          <Table.Cell>
                            <p>
                              {powerSourceData?.find(
                                (e: any) => e.value == item.powerSourceId
                              ).text ?? ""}
                            </p>
                          </Table.Cell>
                          <Table.Cell>
                            <p>{item?.waterAmount ?? ""}</p>
                          </Table.Cell>
                          <Table.Cell>
                            <p>{item?.appliedPortion ?? ""}</p>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </CommonTable>
                </Grid.Column>
              )}
            </Grid.Column>
            <Grid.Column computer={8} tablet={16} mobile={16}>
              <Grid>
                <Grid.Column computer={4} tablet={8} mobile={16}>
                  <DropDown
                    labelName={"Irrigation Method"}
                    placeholder="Select Irrigation Method"
                    defaultValue={irrigation}
                    currentData={irrigationData ? irrigationData : []}
                    handleChangeState={(e: any, { value }: any) => {
                      setIrrigation(value);
                    }}
                    search
                    key={"irrigation_name"}
                  />
                </Grid.Column>
                <Grid.Column computer={4} tablet={8} mobile={16}>
                  <DropDown
                    labelName={"Power Source"}
                    placeholder="Select Power Source"
                    defaultValue={powerSource}
                    currentData={powerSourceData}
                    handleChangeState={(e: any, { value }: any) => {
                      setPowerSource(value);
                    }}
                    search
                    key={"power_source"}
                  />
                </Grid.Column>
                <Grid.Column computer={3} tablet={6} mobile={16}>
                  <InputNumber
                    transform={{
                      input: (value: any) =>
                        isNaN(value) || value === 0 ? "" : value.toString(),
                      output: (e: any) => {
                        return e;
                      },
                    }}
                    control={control}
                    labelName={"Water Amount (m3)"}
                    placeholder="Water Amount (m3)"
                    name="waterAmount"
                    required
                    errors={errors?.waterAmount}
                    errorMessage={"Water Amount is required"}
                    otherValidation={{
                      validate: {
                        valueCheck: (v: any) => validateCultivationNumber(v)
                      }
                    }}
                  />
                </Grid.Column>
                <Grid.Column computer={3} tablet={6} mobile={16}>
                  <InputNumber
                    transform={{
                      input: (value: any) =>
                        isNaN(value) || value === 0 ? "" : value.toString(),
                      output: (e: any) => {
                        return e;
                      },
                    }}
                    control={control}
                    labelName={"Applied Portion ( % )"}
                    placeholder="Applied Portion ( % )"
                    name="appliedPortion"
                    required
                    errorMessage={"Applied Portion is required"}
                    errors={errors?.appliedPortion}
                    otherValidation={{
                      validate: {
                        valueCheck: (v: any) =>
                          isCheckPercentageIrrigation(v) ||
                          "Applied Portion is not valid",
                      },
                    }}
                  />
                </Grid.Column>
                <Grid.Column computer={2} tablet={2} mobile={16}>
                  {enableUpdateButton ?
                    <CustomButton
                      theme="green"
                      title={enableUpdateButton ? "Update" : ""}
                      icon={!enableUpdateButton ? "plus" : null}
                      disabled={isEmpty(irrigation) || isEmpty(powerSource)}
                      customColumnStyle={"updateIrrgation"}
                      onClick={() => {
                        AddIrrigation();
                        setEnableUpdateButton(false);
                      }}
                    /> :
                    <CustomButton
                      theme="green"
                      customColumnStyle={"addUnitSize"}
                      icon
                      iconChild={<PlusIcon />}
                      customBase="productUnitAddButton"
                      onClick={() => {
                        AddIrrigation();
                        setEnableUpdateButton(false);
                      }}
                    />
                  }
                </Grid.Column>
              </Grid>
              {irrigationListData?.length > 0 && (
                <Grid.Column
                  computer={16}
                  tablet={16}
                  mobile={16}
                  className="pesticideTable"
                >
                  <CommonTable
                    tableHeaderData={IrrigationTableHeader}
                    singleLine={false}
                    className="pesticideTable"
                  >
                    {irrigationListData?.map((item: any, index: any) => {
                      return (
                        <Table.Row key={`pesticide_${item?.id}`}>
                          <Table.Cell>
                            <p>
                              {irrigationData?.find(
                                (e: any) => e.value == item.irrigationMethodId
                              ).text ?? ""}
                            </p>
                          </Table.Cell>
                          <Table.Cell>
                            <p>
                              {powerSourceData?.find(
                                (e: any) => e.value == item.powerSourceId
                              ).text ?? ""}
                            </p>
                          </Table.Cell>
                          <Table.Cell>
                            <p>{item?.waterAmount ?? ""}</p>
                          </Table.Cell>
                          <Table.Cell>
                            <p>{item?.appliedPortion ?? ""}</p>
                          </Table.Cell>
                          <Table.Cell>
                            <Grid>
                              <Grid.Column computer={8} tablet={16} mobile={16}>
                                <MdModeEditOutline
                                  cursor={"pointer"}
                                  size={24}
                                  color="var(--tableEditIcon)"
                                  onClick={() => {
                                    onEditHandler(item, index);
                                    setEnableUpdateButton(true);
                                  }}
                                />
                              </Grid.Column>
                              <Grid.Column computer={8} tablet={16} mobile={16}>
                                <MdDeleteOutline
                                  cursor={"pointer"}
                                  size={24}
                                  color="var(--tableEditIcon)"
                                  onClick={() => {
                                    setVisibleDeleteModal(true);
                                    setDeleteValue({
                                      type: "Irrigation",
                                      item: item ?? "",
                                      index: index,
                                    });
                                  }}
                                />
                              </Grid.Column>
                            </Grid>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </CommonTable>
                </Grid.Column>
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
      <ConfirmModal
        viewModal={visibleDeleteModal}
        closeModal={() => setVisibleDeleteModal(false)}
        cancel={() => {
          setVisibleDeleteModal(false);
          setDeleteValue({});
        }}
        approve={deleteIrrigation}
        title={`Delete ${deleteValue.type}`}
        subTitle={`Are you sure you want to delete Irrigation data?`}
      />
    </div>
  ) : null;
})

export default Irrigation;
